/** StatCard (§16.2 tiles): real numbers only; `value === null` renders an honest "No data yet". */
import { Skeleton } from '@windrop/ui';
import type { ReactNode } from 'react';
import { NoDataYet } from './EmptyState';
import { ToneBadge, type Tone } from './StatusBadge';

export interface StatCardProps {
  label: string;
  /** Formatted value; null = nothing reported yet. */
  value: ReactNode | null;
  /** Small text under the value (period, unit, denominator). */
  hint?: ReactNode;
  /** Secondary signal with its own text (e.g. "3 overdue"). */
  signal?: { tone: Tone; text: string };
  loading?: boolean;
  /** Link or button to the console that owns this number. */
  action?: ReactNode;
  /** What is missing when there is no data — keeps the tile honest. */
  emptyHint?: string;
  icon?: ReactNode;
  'data-testid'?: string;
}

export function StatCard({
  label,
  value,
  hint,
  signal,
  loading,
  action,
  emptyHint,
  icon,
  ...rest
}: StatCardProps) {
  return (
    <section className="wdc-stat" aria-busy={loading || undefined} data-testid={rest['data-testid']}>
      <h3 className="wdc-stat__label">
        {icon}
        {label}
      </h3>
      {loading ? (
        <Skeleton shape="display" width="60%" height={30} label={`Loading ${label}`} />
      ) : value === null ? (
        <NoDataYet what={label.toLowerCase()} hint={emptyHint} />
      ) : (
        <p
          className="wdc-stat__value wd-tabular"
          data-long={typeof value === 'string' && value.length > 14 ? 'true' : undefined}
        >
          {value}
        </p>
      )}
      {hint && !loading && value !== null ? <p className="wdc-stat__hint">{hint}</p> : null}
      {(signal && !loading) || action ? (
        <div className="wdc-stat__foot">
          {signal && !loading ? <ToneBadge tone={signal.tone}>{signal.text}</ToneBadge> : <span />}
          {action ? <div className="wdc-stat__action">{action}</div> : null}
        </div>
      ) : null}
    </section>
  );
}
